import React, { FC, useState } from 'react';
import clsx from 'clsx';
import { ShiftStatusType } from 'const/ShiftConst';
import { Dayjs } from 'dayjs';
import { GoTools } from 'react-icons/go';
import { CourierJson } from 'types/bundleTypes';
import {
  createCourierShift,
  ExtraWork,
  IShift,
  removeShifts,
  updateShiftStatus,
} from './shiftApi';
import {
  MdContentCopy,
  MdContentPaste,
  MdEventAvailable,
  MdEventBusy,
} from 'react-icons/md';
import './ActionPanel.scss';
import { HiOutlinePencil } from 'react-icons/hi';
import ShiftNoteModal from './modals/ShiftNoteModal';
import ShiftChangeModal from './modals/ShiftChangeModal';
import CourierPickerModal from './modals/CourierPickerModal';
import { TooltipComponent } from 'ReusableComponents/Tooltip/TooltipComponent';
import ActiveNoteIcon from 'assets/svg/ActiveNoteIcon';
import { IoBagSharp } from 'react-icons/all';
import { capitalize } from '../../utils/strings';
import { handleError } from './utils';

export enum ShiftColor {
  RED,
  GREY,
  YELLOW,
  GREEN,
  PURPLE,
  BLUE,
}

const ActionPanel: FC<{
  date: Dayjs;
  shiftData: IShift;
  courier?: CourierJson;
  shiftColor: ShiftColor;
  wishList: number[];
  couriers: CourierJson[];
}> = ({ date, shiftData, courier, shiftColor, wishList, couriers }) => (
  <div className="actionPanel">
    {(() => {
      switch (shiftColor) {
        case ShiftColor.GREY:
        case ShiftColor.PURPLE:
        case ShiftColor.RED:
          return (
            <>
              <ChangeNoteAction
                className="actionPanel__firstAction"
                shiftColor={ShiftColor[shiftColor]}
                shiftId={shiftData.id}
                note={shiftData.note}
              />
              <RecoverShiftAction
                shiftColor={ShiftColor[shiftColor]}
                shiftId={shiftData.id}
              />
            </>
          );
        case ShiftColor.YELLOW:
          return (
            <>
              <AcceptBookingAction
                className="actionPanel__firstAction"
                shiftColor={ShiftColor[shiftColor]}
                shiftId={shiftData.id}
              />
              <DuplicateRequestAction
                shiftColor={ShiftColor[shiftColor]}
                courierId={courier?.id}
                date={date}
              />
              <ChangeNoteAction
                shiftColor={ShiftColor[shiftColor]}
                shiftId={shiftData.id}
                note={shiftData.note}
              />
              <ShiftChangeAction
                shiftColor={ShiftColor[shiftColor]}
                shiftId={shiftData.id}
                courierName={courier?.fullName ?? ''}
              />
            </>
          );
        case ShiftColor.GREEN:
          return (
            <>
              {shiftData.extraWork.length > 0 && (
                <ShowExtraWorkAction
                  shiftColor={ShiftColor[shiftColor]}
                  extraWork={shiftData.extraWork}
                />
              )}
              <DuplicateRequestAction
                className="actionPanel__firstAction"
                shiftColor={ShiftColor[shiftColor]}
                courierId={courier?.id}
                date={date}
              />
              <ChangeNoteAction
                shiftColor={ShiftColor[shiftColor]}
                shiftId={shiftData.id}
                note={shiftData.note}
              />
              <ShiftChangeAction
                shiftColor={ShiftColor[shiftColor]}
                shiftId={shiftData.id}
                courierName={courier?.fullName ?? ''}
              />
            </>
          );
        case ShiftColor.BLUE:
          return (
            <>
              <AssignCourierAction
                shiftId={shiftData.id}
                date={date}
                wishList={wishList}
                couriers={couriers}
              />
              <ChangeNoteAction
                shiftColor={ShiftColor[shiftColor]}
                shiftId={shiftData.id}
                note={shiftData.note}
              />
              <ShiftChangeAction
                shiftColor={ShiftColor[shiftColor]}
                shiftId={shiftData.id}
                courierName={courier?.fullName ?? ''}
                onlyDelete
              />
            </>
          );
      } // switch
    })()}
  </div>
);

// Request the same courier next day
const DuplicateRequestAction: FC<{
  className?: string;
  shiftColor: string;
  courierId: number;
  date: Dayjs;
}> = ({ className, shiftColor, courierId, date }) => {
  const duplicateShiftForDate = () => {
    createCourierShift(date.add(1, 'day'), courierId).catch(handleError);
  };

  return (
    <TooltipComponent id="duplicate-action" title="Duplicate">
      <div
        className={clsx(
          'actionPanel__action',
          `actionPanel__bgcolor__${shiftColor}`,
          className
        )}
        onClick={duplicateShiftForDate}
      >
        <MdContentCopy />
      </div>
    </TooltipComponent>
  );
};

const AssignCourierAction: FC<{
  className?: string;
  shiftId: string;
  date: Dayjs;
  wishList: number[];
  couriers: CourierJson[];
}> = ({ className, shiftId, date, wishList, couriers }) => {
  const [showModal, setShowModal] = useState(false);

  return (
    <>
      {showModal && (
        <CourierPickerModal
          shiftId={shiftId}
          date={date}
          wishList={wishList}
          couriers={couriers}
          showModal={showModal}
          setShowModal={setShowModal}
        />
      )}
      <TooltipComponent id="assign-courier-action" title="Assign courier">
        <div
          className={clsx('actionPanel__action', className)}
          onClick={() => setShowModal(true)}
        >
          <HiOutlinePencil />
        </div>
      </TooltipComponent>
    </>
  );
};

const AcceptBookingAction: FC<{
  className?: string;
  shiftColor: string;
  shiftId: string;
}> = ({ className, shiftColor, shiftId }) => {
  return (
    <TooltipComponent id="accept-booking-action" title="Accept booking">
      <div
        className={clsx(
          'actionPanel__action',
          `actionPanel__bgcolor__${shiftColor}`,
          className
        )}
        onClick={() =>
          updateShiftStatus(shiftId, ShiftStatusType.BOOKED).catch(handleError)
        }
      >
        <MdEventAvailable />
      </div>
    </TooltipComponent>
  );
};

const ChangeNoteAction: FC<{
  className?: string;
  shiftColor: string;
  shiftId: string;
  note?: string;
}> = ({ className, shiftColor, shiftId, note }) => {
  const [showModal, setShowModal] = useState(false);

  return (
    <>
      {showModal && (
        <ShiftNoteModal
          shiftId={shiftId}
          currentNote={note}
          showModal={showModal}
          setShowModal={setShowModal}
        />
      )}
      <TooltipComponent id="update-note-action" title="Change note">
        <div
          className={clsx(
            'actionPanel__action',
            `actionPanel__bgcolor__${shiftColor}`,
            className
          )}
          onClick={() => setShowModal(true)}
        >
          {note?.length ? (
            <ActiveNoteIcon className="actionPanel__action__active-note" />
          ) : (
            <MdContentPaste />
          )}
        </div>
      </TooltipComponent>
    </>
  );
};

const RecoverShiftAction: FC<{
  className?: string;
  shiftColor: string;
  shiftId: string;
}> = ({ className, shiftColor, shiftId }) => {
  return (
    <TooltipComponent id="recover-shift-action" title="Recover shift">
      <div
        className={clsx(
          'actionPanel__action',
          `actionPanel__bgcolor__${shiftColor}`,
          className
        )}
        onClick={() =>
          updateShiftStatus(shiftId, ShiftStatusType.REQUESTED).catch(
            handleError
          )
        }
      >
        <GoTools />
      </div>
    </TooltipComponent>
  );
};

const ShiftChangeAction: FC<{
  className?: string;
  shiftColor: string;
  shiftId: string;
  courierName: string;
  onlyDelete?: boolean;
}> = ({ className, shiftColor, shiftId, courierName, onlyDelete }) => {
  const [showModal, setShowModal] = useState(false);

  const handleShiftChange = () => {
    courierName
      ? setShowModal(true)
      : removeShifts([shiftId]).catch(handleError);
  };

  return (
    <>
      {showModal && (
        <ShiftChangeModal
          shiftId={shiftId}
          courierName={courierName}
          showModal={showModal}
          setShowModal={setShowModal}
          onlyDelete={onlyDelete}
        />
      )}
      <TooltipComponent id="shift+change-action" title="Close shift">
        <div
          className={clsx(
            'actionPanel__action',
            `actionPanel__bgcolor__${shiftColor}`,
            className
          )}
          onClick={handleShiftChange}
        >
          <MdEventBusy />
        </div>
      </TooltipComponent>
    </>
  );
};

const ShowExtraWorkAction: FC<{
  className?: string;
  shiftColor: string;
  extraWork: ExtraWork[];
}> = ({ className, shiftColor, extraWork }) => {
  const extraWorkList = (
    <div className="actionPanel__extra-work">
      {extraWork.map(work => {
        return (
          <p key={`${work.typeOfExtraWork}-${work.payment}`}>
            <span>{capitalize(work.typeOfExtraWork)}</span>
            <span>{`${work.payment},-`}</span>
          </p>
        );
      })}
    </div>
  );

  return (
    <>
      <TooltipComponent id="shift-extra-work" title={extraWorkList}>
        <div
          className={clsx(
            'actionPanel__action',
            `actionPanel__bgcolor__${shiftColor}`,
            className,
            'actionPanel__rounded'
          )}
        >
          <IoBagSharp />
        </div>
      </TooltipComponent>
    </>
  );
};

export default ActionPanel;
